import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import emailjs from '@emailjs/browser';

const DonatePage = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    zipCode: '',
    email: '',
    nextOfKinName: '',
    nextOfKinRelationship: '',
    nextOfKinEmail: '',
    preferNotToDesignateNextOfKin: false,
    purposeConsent: {
      artisticInstallations: false,
      outdoorGallery: false,
      documentation: false,
      publicAccess: false,
    },
    ethicalAcknowledgment: false,
    purposeEthicalAcknowledgment: false,
    revocationAcknowledgment: false,
    costsAcknowledgment: false,
    signature: '',
    signatureDate: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');
  const [emailErrors, setEmailErrors] = useState({
    email: '',
    nextOfKinEmail: '',
  });
  const [submitError, setSubmitError] = useState('');

  const totalPages = 4;

  // Email validation function
  const validateEmail = (email) => {
    if (!email) return true;
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;
    if (!emailRegex.test(email)) return false;
    const parts = email.split('@');
    if (parts.length !== 2) return false;
    const domain = parts[1];
    const domainParts = domain.split('.');
    if (domainParts.length < 2) return false;
    const tld = domainParts[domainParts.length - 1];
    return tld.length >= 3 && /^[a-zA-Z0-9-]+$/.test(tld);
  };

  // Load form data from localStorage on mount
  useEffect(() => {
    const savedData = localStorage.getItem('massBodyDonationForm');
    if (savedData) {
      try {
        setFormData(JSON.parse(savedData));
      } catch (e) {
        console.error('Error loading saved form data:', e);
      }
    }
  }, []);

  // Save form data to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('massBodyDonationForm', JSON.stringify(formData));
  }, [formData]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'email' || name === 'nextOfKinEmail') {
      if (emailErrors[name]) {
        setEmailErrors(prev => ({
          ...prev,
          [name]: ''
        }));
      }
      if (submitError) {
        setSubmitError('');
      }
    }
    
    if (name === 'preferNotToDesignateNextOfKin' && checked) {
      setFormData(prev => ({
        ...prev,
        preferNotToDesignateNextOfKin: true,
        nextOfKinName: '',
        nextOfKinRelationship: '',
        nextOfKinEmail: '',
      }));
      setEmailErrors(prev => ({
        ...prev,
        nextOfKinEmail: ''
      }));
      return;
    }
    
    if ((name === 'nextOfKinName' || name === 'nextOfKinRelationship' || name === 'nextOfKinEmail') && value && formData.preferNotToDesignateNextOfKin) {
      setFormData(prev => ({
        ...prev,
        preferNotToDesignateNextOfKin: false,
        [name]: value
      }));
      return;
    }
    
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: type === 'checkbox' ? checked : value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleEmailBlur = (e) => {
    // Don't validate on blur - only validate on submit
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      window.scrollTo(0, 0);
    }
  };

  const handleBack = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      window.scrollTo(0, 0);
    } else {
      navigate(-1);
    }
  };

  const handleHome = () => {
    navigate('/');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setSubmitError('');
    setEmailErrors({ email: '', nextOfKinEmail: '' });
    
    const emailValid = validateEmail(formData.email);
    const nextOfKinEmailValid = !formData.nextOfKinEmail || validateEmail(formData.nextOfKinEmail);
    
    if (!emailValid || !nextOfKinEmailValid) {
      setSubmitError('Some of the entered information is invalid');
      
      const newErrors = { email: '', nextOfKinEmail: '' };
      if (!emailValid) {
        newErrors.email = 'Please enter a valid email address';
      }
      if (!nextOfKinEmailValid && formData.nextOfKinEmail) {
        newErrors.nextOfKinEmail = 'Please enter a valid email address';
      }
      setEmailErrors(newErrors);
      
      window.scrollTo(0, 0);
      setIsSubmitting(false);
      return;
    }
    
    setIsSubmitting(true);
    setSubmitStatus('sending');

    const emailContent = `
MASS BODY Project – Body Donation Agreement

TO: donate@massbody.org

DONOR INFORMATION:
Name: ${formData.name}
Zip Code: ${formData.zipCode}
Email: ${formData.email}
Next of Kin/Legal Representative:
${formData.preferNotToDesignateNextOfKin 
  ? '  Prefer not to designate at this time' 
  : `  Name: ${formData.nextOfKinName || 'Not provided'}
  Relationship: ${formData.nextOfKinRelationship || 'Not provided'}
  Email: ${formData.nextOfKinEmail || 'Not provided'}`}

PURPOSE OF DONATION:
The donor has reviewed and understands that their body will be used for:
• Artistic installations and studies at the MASS BODY site
• Placement in a designated outdoor "gallery plot" for natural decomposition
• Documentation through artistic means (drawing, photography, time-lapse, video, performance)
• Public access and exhibitions as part of ongoing cultural reflection

PURPOSE AND ETHICAL FRAMEWORK ACKNOWLEDGMENT: ${formData.purposeEthicalAcknowledgment ? 'Yes' : 'No'}
REVOCATION ACKNOWLEDGMENT: ${formData.revocationAcknowledgment ? 'Yes' : 'No'}


---
Docusign Envelope ID: 9534CC91-3665-4931-9D1E-8AE6AEE3B01D
    `.trim();

    try {
      emailjs.init('PHHjdnlnzswyhX2_a');
      
      await emailjs.send(
        'service_jh43zkf',
        'template_cab09n5',
        {
          name: formData.name,
          email: formData.email,
          title: 'MASS BODY Donation Agreement Submission',
          message: emailContent
        }
      );

      setSubmitStatus('success');
      localStorage.removeItem('massBodyDonationForm');
      
      setTimeout(() => {
        navigate('/');
      }, 3000);
    } catch (error) {
      console.error('EmailJS Error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const canProceed = () => {
    switch (currentPage) {
      case 1:
        return true;
      case 2:
        return formData.purposeEthicalAcknowledgment;
      case 3:
        return formData.revocationAcknowledgment;
      case 4:
        return formData.name && 
               formData.zipCode && 
               formData.email;
      default:
        return false;
    }
  };

  const renderPage = () => {
    switch (currentPage) {
      case 1:
        return <IntroductionPage />;
      case 2:
        return <PurposeEthicalPage formData={formData} handleInputChange={handleInputChange} />;
      case 3:
        return <RevocationCostsPage formData={formData} handleInputChange={handleInputChange} />;
      case 4:
        return <DonorInfoPage formData={formData} handleInputChange={handleInputChange} handleEmailBlur={handleEmailBlur} emailErrors={emailErrors} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-white relative">
      {/* Full Screen Success Overlay */}
      {submitStatus === 'success' && (
        <div className="fixed inset-0 bg-white z-50 flex items-center justify-center">
          <div className="text-center px-8">
            <h2
              className="text-5xl md:text-6xl lg:text-7xl text-black mb-8 leading-tight"
              style={{
                fontFamily: 'Arial, Helvetica, sans-serif',
                letterSpacing: '-0.03em',
                fontWeight: 300,
                lineHeight: '1.1'
              }}
            >
              Thank You
            </h2>
            <p className="text-lg md:text-xl text-black/60 leading-relaxed max-w-2xl mx-auto" style={{ 
              fontFamily: 'Arial, Helvetica, sans-serif',
              lineHeight: '1.7'
            }}>
              Your donation agreement has been submitted. You will receive an email for scheduling a followup call with a member of our team.
            </p>
            <p className="text-base text-black/40 mt-6" style={{ fontFamily: 'Arial, Helvetica, sans-serif' }}>
              Redirecting...
            </p>
          </div>
        </div>
      )}

      {/* Minimal Header */}
      <div className="w-full border-b border-black/5 py-6">
        <div className="max-w-[1200px] mx-auto px-8 md:px-12 lg:px-16">
          <div className="flex items-center justify-between">
            <button
              onClick={handleHome}
              className="text-black/40 hover:text-black transition-colors text-sm tracking-wide uppercase"
              style={{ 
                fontFamily: 'Arial, Helvetica, sans-serif',
                letterSpacing: '0.1em',
                fontSize: '11px'
              }}
            >
              ← MASS BODY
            </button>
            <div className="flex items-center gap-3">
              {Array.from({ length: totalPages }).map((_, i) => (
                <div
                  key={i}
                  className={`w-1 h-1 rounded-full transition-all duration-300 ${
                    i + 1 <= currentPage ? 'bg-black w-8' : 'bg-black/10'
                  }`}
                />
              ))}
            </div>
            <div className="text-xs text-black/30 uppercase tracking-wider" style={{ fontFamily: 'Arial, Helvetica, sans-serif' }}>
              {currentPage} / {totalPages}
            </div>
          </div>
        </div>
      </div>

      {/* Form Content */}
      <div className="w-full py-20 md:py-32">
        {renderPage()}
      </div>

      {/* Navigation Footer */}
      <div className="w-full border-t border-black/5 py-12">
        <div className="max-w-[1200px] mx-auto px-8 md:px-12 lg:px-16">
          <div className="flex justify-between items-center">
            {currentPage > 1 ? (
              <button
                onClick={handleBack}
                className="text-black/40 hover:text-black transition-colors text-sm uppercase tracking-wider disabled:opacity-20 disabled:cursor-not-allowed"
                style={{
                  fontFamily: 'Arial, Helvetica, sans-serif',
                  letterSpacing: '0.15em',
                  fontSize: '11px'
                }}
              >
                ← Back
              </button>
            ) : (
              <div></div>
            )}
            
            {currentPage < totalPages ? (
              <button
                onClick={handleNext}
                disabled={!canProceed()}
                className="px-12 py-4 border-2 border-black bg-black text-white hover:bg-white hover:text-black transition-all duration-300 text-sm uppercase tracking-wider disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-black disabled:hover:text-white"
                style={{
                  fontFamily: 'Arial, Helvetica, sans-serif',
                  letterSpacing: '0.15em',
                  fontSize: '11px',
                  fontWeight: 400
                }}
              >
                Next →
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={!canProceed() || isSubmitting}
                className="px-12 py-4 border-2 border-black bg-black text-white hover:bg-white hover:text-black transition-all duration-300 text-sm uppercase tracking-wider disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-black disabled:hover:text-white"
                style={{
                  fontFamily: 'Arial, Helvetica, sans-serif',
                  letterSpacing: '0.15em',
                  fontSize: '11px',
                  fontWeight: 400
                }}
              >
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </button>
            )}
          </div>

          {submitError && (
            <div className="mt-8 text-center text-black/60 text-sm" style={{ fontFamily: 'Arial, Helvetica, sans-serif' }}>
              {submitError}
            </div>
          )}
          {submitStatus === 'error' && (
            <div className="mt-8 text-center text-black/60 text-sm" style={{ fontFamily: 'Arial, Helvetica, sans-serif' }}>
              There was an error submitting your form. Please try again.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Page 1: Introduction - Pentagram Style
const IntroductionPage = () => (
  <div className="max-w-[1200px] mx-auto px-8 md:px-12 lg:px-16">
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-20 lg:gap-24">
      <div className="lg:col-span-5">
        <h1
          className="text-5xl md:text-6xl lg:text-7xl text-black mb-12 leading-tight"
          style={{
            fontFamily: 'Arial, Helvetica, sans-serif',
            letterSpacing: '-0.03em',
            fontWeight: 300,
            lineHeight: '1.1'
          }}
        >
          Body Donation Agreement
        </h1>
      </div>
      <div className="lg:col-span-7">
        <div className="space-y-8 text-black/70" style={{ fontFamily: 'Arial, Helvetica, sans-serif' }}>
          <p className="text-lg md:text-xl leading-relaxed" style={{ lineHeight: '1.7' }}>
            Thank you for your interest in donating your body to the MASS BODY Project, an interdisciplinary artistic research and exhibition initiative.
          </p>
          <p className="text-base md:text-lg leading-relaxed" style={{ lineHeight: '1.7' }}>
            This form will capture basic information so a member of our team can contact you to answer questions and organize your donation.
          </p>
          <div className="pt-4">
            <p className="text-base md:text-lg leading-relaxed mb-6" style={{ lineHeight: '1.7' }}>
              The information here covers:
            </p>
            <ul className="space-y-3 text-base md:text-lg leading-relaxed" style={{ lineHeight: '1.7' }}>
              <li className="flex items-start">
                <span className="mr-4 text-black/30">01</span>
                <span>Donor information and contact details</span>
              </li>
              <li className="flex items-start">
                <span className="mr-4 text-black/30">02</span>
                <span>Purpose and scope of the donation</span>
              </li>
              <li className="flex items-start">
                <span className="mr-4 text-black/30">03</span>
                <span>Ethical framework and treatment</span>
              </li>
              <li className="flex items-start">
                <span className="mr-4 text-black/30">04</span>
                <span>Revocation rights and costs</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Page 4: Donor Information - Pentagram Style
const DonorInfoPage = ({ formData, handleInputChange, handleEmailBlur, emailErrors }) => (
  <div className="max-w-[1200px] mx-auto px-8 md:px-12 lg:px-16">
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-20 lg:gap-24 mb-24">
      <div className="lg:col-span-5">
        <h2
          className="text-5xl md:text-6xl lg:text-7xl text-black mb-12 leading-tight"
          style={{
            fontFamily: 'Arial, Helvetica, sans-serif',
            letterSpacing: '-0.03em',
            fontWeight: 300,
            lineHeight: '1.1'
          }}
        >
          Donor Information
        </h2>
        <p className="text-base text-black/50 leading-relaxed" style={{ 
          fontFamily: 'Arial, Helvetica, sans-serif',
          lineHeight: '1.6'
        }}>
          Please share your name, email, and zip code. We'll use your name and email to contact you and schedule a call. We use your zip code to check if we can accept your donation based on location.
        </p>
      </div>
      <div className="lg:col-span-7">
        <div className="space-y-12">
          <div>
            <label className="block text-xs text-black/40 uppercase tracking-wider mb-4" style={{ 
              fontFamily: 'Arial, Helvetica, sans-serif',
              letterSpacing: '0.15em'
            }}>
              Full Name
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className="w-full bg-transparent border-b border-black/20 text-black text-lg py-4 focus:border-black focus:outline-none transition-colors"
              style={{ 
                fontFamily: 'Arial, Helvetica, sans-serif',
                fontWeight: 400
              }}
              required
            />
          </div>
          
          <div>
            <label className="block text-xs text-black/40 uppercase tracking-wider mb-4" style={{ 
              fontFamily: 'Arial, Helvetica, sans-serif',
              letterSpacing: '0.15em'
            }}>
              Zip Code
            </label>
            <input
              type="text"
              name="zipCode"
              value={formData.zipCode}
              onChange={handleInputChange}
              className="w-full bg-transparent border-b border-black/20 text-black text-lg py-4 focus:border-black focus:outline-none transition-colors"
              style={{ 
                fontFamily: 'Arial, Helvetica, sans-serif',
                fontWeight: 400
              }}
              placeholder="12345"
              required
            />
            <p className="mt-3 text-sm text-black/40" style={{ fontFamily: 'Arial, Helvetica, sans-serif' }}>
              At this time we are only accepting interest for persons based in the US
            </p>
          </div>
          
          <div>
            <label className="block text-xs text-black/40 uppercase tracking-wider mb-4" style={{ 
              fontFamily: 'Arial, Helvetica, sans-serif',
              letterSpacing: '0.15em'
            }}>
              Email
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              onBlur={handleEmailBlur}
              className={`w-full bg-transparent border-b text-black text-lg py-4 focus:outline-none transition-colors ${
                emailErrors.email 
                  ? 'border-black/40' 
                  : 'border-black/20 focus:border-black'
              }`}
              style={{ 
                fontFamily: 'Arial, Helvetica, sans-serif',
                fontWeight: 400
              }}
              required
            />
            {emailErrors.email && (
              <p className="mt-3 text-sm text-black/40" style={{ fontFamily: 'Arial, Helvetica, sans-serif' }}>
                {emailErrors.email}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
    
    {/* Next of Kin Section */}
    <div className="pt-24 border-t border-black/5">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-20 lg:gap-24 mb-24">
        <div className="lg:col-span-5">
          <h2
            className="text-4xl md:text-5xl lg:text-6xl text-black mb-12 leading-tight"
            style={{
              fontFamily: 'Arial, Helvetica, sans-serif',
              letterSpacing: '-0.03em',
              fontWeight: 300,
              lineHeight: '1.1'
            }}
          >
            Next of Kin or Legal Representative
          </h2>
          <p className="text-base text-black/50 leading-relaxed" style={{ 
            fontFamily: 'Arial, Helvetica, sans-serif',
            lineHeight: '1.6'
          }}>
            We ask for a Next of Kin or Legal Representative so that we can begin the process of making your wishes visible to those whose participation may be needed at the time of donation.
          </p>
        </div>
        <div className="lg:col-span-7">
          <div className="space-y-12">
            <div className={`space-y-12 ${formData.preferNotToDesignateNextOfKin ? 'opacity-30 pointer-events-none' : ''}`}>
              <div>
                <label className="block text-xs text-black/40 uppercase tracking-wider mb-4" style={{ 
                  fontFamily: 'Arial, Helvetica, sans-serif',
                  letterSpacing: '0.15em'
                }}>
                  Name
                </label>
                <input
                  type="text"
                  name="nextOfKinName"
                  value={formData.nextOfKinName}
                  onChange={handleInputChange}
                  disabled={formData.preferNotToDesignateNextOfKin}
                  className="w-full bg-transparent border-b border-black/20 text-black text-lg py-4 focus:border-black focus:outline-none transition-colors disabled:opacity-30"
                  style={{ 
                    fontFamily: 'Arial, Helvetica, sans-serif',
                    fontWeight: 400
                  }}
                />
              </div>
              
              <div>
                <label className="block text-xs text-black/40 uppercase tracking-wider mb-4" style={{ 
                  fontFamily: 'Arial, Helvetica, sans-serif',
                  letterSpacing: '0.15em'
                }}>
                  Relationship
                </label>
                <input
                  type="text"
                  name="nextOfKinRelationship"
                  value={formData.nextOfKinRelationship}
                  onChange={handleInputChange}
                  disabled={formData.preferNotToDesignateNextOfKin}
                  className="w-full bg-transparent border-b border-black/20 text-black text-lg py-4 focus:border-black focus:outline-none transition-colors disabled:opacity-30"
                  style={{ 
                    fontFamily: 'Arial, Helvetica, sans-serif',
                    fontWeight: 400
                  }}
                  placeholder="e.g., Spouse, Child, Parent"
                />
              </div>
              
              <div>
                <label className="block text-xs text-black/40 uppercase tracking-wider mb-4" style={{ 
                  fontFamily: 'Arial, Helvetica, sans-serif',
                  letterSpacing: '0.15em'
                }}>
                  Email
                </label>
                <input
                  type="email"
                  name="nextOfKinEmail"
                  value={formData.nextOfKinEmail}
                  onChange={handleInputChange}
                  onBlur={handleEmailBlur}
                  disabled={formData.preferNotToDesignateNextOfKin}
                  className={`w-full bg-transparent border-b text-black text-lg py-4 focus:outline-none transition-colors disabled:opacity-30 ${
                    emailErrors.nextOfKinEmail 
                      ? 'border-black/40' 
                      : 'border-black/20 focus:border-black'
                  }`}
                  style={{ 
                    fontFamily: 'Arial, Helvetica, sans-serif',
                    fontWeight: 400
                  }}
                />
                {emailErrors.nextOfKinEmail && !formData.preferNotToDesignateNextOfKin && (
                  <p className="mt-3 text-sm text-black/40" style={{ fontFamily: 'Arial, Helvetica, sans-serif' }}>
                    {emailErrors.nextOfKinEmail}
                  </p>
                )}
              </div>
            </div>
            
            <label className="flex items-start gap-4 cursor-pointer pt-8 border-t border-black/5">
              <input
                type="checkbox"
                name="preferNotToDesignateNextOfKin"
                checked={formData.preferNotToDesignateNextOfKin}
                onChange={handleInputChange}
                className="mt-1 w-4 h-4 border border-black/20 focus:border-black focus:outline-none cursor-pointer"
                style={{ 
                  accentColor: 'black'
                }}
              />
              <span className="text-base leading-relaxed text-black/70" style={{ 
                fontFamily: 'Arial, Helvetica, sans-serif',
                lineHeight: '1.6'
              }}>
                I prefer not to designate a legal representative or next of kin at this time
              </span>
            </label>
          </div>
        </div>
      </div>
    </div>
    
    {/* Followup Message */}
    <div className="pt-24 border-t border-black/5">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-20 lg:gap-24">
        <div className="lg:col-span-5"></div>
        <div className="lg:col-span-7">
          <p className="text-base leading-relaxed text-black/60" style={{ 
            fontFamily: 'Arial, Helvetica, sans-serif',
            lineHeight: '1.7'
          }}>
            After submitting your information, you will receive an email for scheduling a followup call with a member of our team to answer questions and proceed in the donation process.
          </p>
        </div>
      </div>
    </div>
  </div>
);

// Page 2: Purpose of Donation + Ethical Framework - Pentagram Style
const PurposeEthicalPage = ({ formData, handleInputChange }) => (
  <div className="max-w-[1200px] mx-auto px-8 md:px-12 lg:px-16">
    {/* Purpose Section */}
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-20 lg:gap-24 mb-32">
      <div className="lg:col-span-5">
        <h2
          className="text-5xl md:text-6xl lg:text-7xl text-black mb-12 leading-tight"
          style={{
            fontFamily: 'Arial, Helvetica, sans-serif',
            letterSpacing: '-0.03em',
            fontWeight: 300,
            lineHeight: '1.1'
          }}
        >
          Purpose of Donation
        </h2>
      </div>
      <div className="lg:col-span-7">
        <div className="space-y-8 text-black/70" style={{ fontFamily: 'Arial, Helvetica, sans-serif' }}>
          <p className="text-lg leading-relaxed" style={{ lineHeight: '1.7' }}>
            I understand that my body will be used to:
          </p>
          <ul className="space-y-6 pt-4">
            <li className="flex items-start">
              <span className="mr-6 text-black/30 text-sm mt-1">01</span>
              <span className="text-base leading-relaxed" style={{ lineHeight: '1.7' }}>
                Serve as part of artistic installations and studies at the MASS BODY site, modeled after sculpture gardens and outdoor art centers such as the Chinati Foundation and Storm King Art Center.
              </span>
            </li>
            <li className="flex items-start">
              <span className="mr-6 text-black/30 text-sm mt-1">02</span>
              <span className="text-base leading-relaxed" style={{ lineHeight: '1.7' }}>
                Be placed in a designated outdoor "gallery plot" within the MASS BODY sculpture garden, where the natural processes of decomposition and putrefaction will take place without dissection or preservation.
              </span>
            </li>
            <li className="flex items-start">
              <span className="mr-6 text-black/30 text-sm mt-1">03</span>
              <span className="text-base leading-relaxed" style={{ lineHeight: '1.7' }}>
                Be documented through artistic means, including drawing, photography, time-lapse, video, and performance-based studies.
              </span>
            </li>
            <li className="flex items-start">
              <span className="mr-6 text-black/30 text-sm mt-1">04</span>
              <span className="text-base leading-relaxed" style={{ lineHeight: '1.7' }}>
                Be accessible to approved visitors and researchers as part of an ongoing program of public exhibitions and cultural reflection.
              </span>
            </li>
          </ul>
          <p className="text-base leading-relaxed mt-8 text-black/50 italic" style={{ lineHeight: '1.7' }}>
            The focus of MASS BODY is not medical or forensic, but rather aesthetic eco-exploration of the human form in relation to land, microorganisms, fungi, bacteria, and natural cycles of the human body.
          </p>
        </div>
      </div>
    </div>

    {/* Ethical Framework Section */}
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-20 lg:gap-24 pt-24 border-t border-black/5">
      <div className="lg:col-span-5">
        <h2
          className="text-5xl md:text-6xl lg:text-7xl text-black mb-12 leading-tight"
          style={{
            fontFamily: 'Arial, Helvetica, sans-serif',
            letterSpacing: '-0.03em',
            fontWeight: 300,
            lineHeight: '1.1'
          }}
        >
          Ethical Framework
        </h2>
      </div>
      <div className="lg:col-span-7">
        <div className="space-y-8 text-black/70" style={{ fontFamily: 'Arial, Helvetica, sans-serif' }}>
          <p className="text-lg leading-relaxed" style={{ lineHeight: '1.7' }}>
            I acknowledge and understand:
          </p>
          <ul className="space-y-6 pt-4">
            <li className="flex items-start">
              <span className="mr-6 text-black/30 text-sm mt-1">01</span>
              <span className="text-base leading-relaxed" style={{ lineHeight: '1.7' }}>
                My body will be treated with dignity and respect at all times.
              </span>
            </li>
            <li className="flex items-start">
              <span className="mr-6 text-black/30 text-sm mt-1">02</span>
              <span className="text-base leading-relaxed" style={{ lineHeight: '1.7' }}>
                No dissection or invasive procedures will occur after arrival at the MASS BODY site.
              </span>
            </li>
            <li className="flex items-start">
              <span className="mr-6 text-black/30 text-sm mt-1">03</span>
              <span className="text-base leading-relaxed" style={{ lineHeight: '1.7' }}>
                The donation is for artistic and cultural purposes only, not for medical treatment, forensic research, or commercial trade in human remains.
              </span>
            </li>
            <li className="flex items-start">
              <span className="mr-6 text-black/30 text-sm mt-1">04</span>
              <span className="text-base leading-relaxed" style={{ lineHeight: '1.7' }}>
                The MASS BODY Project seeks to expand collective perspectives on metabolism and the body, and embraces decomposition as a powerful artistic medium shared by all living beings.
              </span>
            </li>
          </ul>
          <div className="pt-12 border-t border-black/5">
            <label className="flex items-start gap-4 cursor-pointer">
              <input
                type="checkbox"
                name="purposeEthicalAcknowledgment"
                checked={formData.purposeEthicalAcknowledgment}
                onChange={handleInputChange}
                className="mt-1 w-4 h-4 border border-black/20 focus:border-black focus:outline-none cursor-pointer"
                style={{ 
                  accentColor: 'black'
                }}
              />
              <div>
                <span className="text-base leading-relaxed text-black/80" style={{ 
                  fontFamily: 'Arial, Helvetica, sans-serif',
                  lineHeight: '1.7'
                }}>
                  I acknowledge and have read the purpose of the donation and the ethical framework outlined above.
                </span>
                <p className="text-sm text-black/40 mt-3" style={{ 
                  fontFamily: 'Arial, Helvetica, sans-serif',
                  lineHeight: '1.6'
                }}>
                  We ask for this acknowledgement before scheduling a call with a member of our team.
                </p>
              </div>
            </label>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Page 3: Revocation and Costs - Pentagram Style
const RevocationCostsPage = ({ formData, handleInputChange }) => (
  <div className="max-w-[1200px] mx-auto px-8 md:px-12 lg:px-16">
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-20 lg:gap-24">
      <div className="lg:col-span-5">
        <h2
          className="text-5xl md:text-6xl lg:text-7xl text-black mb-12 leading-tight"
          style={{
            fontFamily: 'Arial, Helvetica, sans-serif',
            letterSpacing: '-0.03em',
            fontWeight: 300,
            lineHeight: '1.1'
          }}
        >
          Revocation & Costs
        </h2>
      </div>
      <div className="lg:col-span-7">
        <div className="space-y-16 text-black/70" style={{ fontFamily: 'Arial, Helvetica, sans-serif' }}>
          <div>
            <h3 className="text-xl text-black mb-6 font-normal" style={{ 
              fontFamily: 'Arial, Helvetica, sans-serif',
              letterSpacing: '-0.01em'
            }}>
              Revocation of Consent
            </h3>
            <p className="text-base leading-relaxed mb-4" style={{ lineHeight: '1.7' }}>
              I may revoke my consent at any time during my lifetime by submitting written notice to the MASS BODY Project. Upon receipt of such notice, my name will be removed from the donor registry.
            </p>
            <p className="text-base leading-relaxed" style={{ lineHeight: '1.7' }}>
              The MASS BODY Project also reserves the right to decline acceptance of my body at the time of death if legal, ethical, or logistical circumstances prevent donation.
            </p>
          </div>
          <div>
            <h3 className="text-xl text-black mb-6 font-normal" style={{ 
              fontFamily: 'Arial, Helvetica, sans-serif',
              letterSpacing: '-0.01em'
            }}>
              Costs
            </h3>
            <p className="text-base leading-relaxed" style={{ lineHeight: '1.7' }}>
              There is no cost to me or my family for donating my body to MASS BODY. The Project will cover all expenses related to transportation and placement of the body.
            </p>
          </div>
          <div className="pt-8 border-t border-black/5">
            <label className="flex items-start gap-4 cursor-pointer">
              <input
                type="checkbox"
                name="revocationAcknowledgment"
                checked={formData.revocationAcknowledgment}
                onChange={handleInputChange}
                className="mt-1 w-4 h-4 border border-black/20 focus:border-black focus:outline-none cursor-pointer"
                style={{ 
                  accentColor: 'black'
                }}
              />
              <span className="text-base leading-relaxed text-black/80" style={{ 
                fontFamily: 'Arial, Helvetica, sans-serif',
                lineHeight: '1.7'
              }}>
                I understand my right to revoke consent and the Project's right to decline acceptance.
              </span>
            </label>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default DonatePage;
